/**
 * sandboxUrlSync — save a hand-built playground structure into the URL on
 * demand (Ctrl+S / Cmd+S), with an explicit enable lifecycle.
 *
 * Lifecycle:
 *   - The save shortcut is DISABLED by default.
 *   - Clicking "Clear playground" ENABLES it (you're starting a fresh sandbox)
 *     and cleans the URL's saved-build params.
 *   - Loading a molecule or a tutorial DISABLES it and cleans the URL.
 *   Both transitions are driven by `notifySystemApplied`, which
 *   `applyMoleculeToViewer` calls for every clear/load (the single chokepoint).
 *
 * Saving (Ctrl/Cmd+S, only while enabled): serializes the current atoms to XYZ
 * and writes `?molxyz=`, capturing the browser's default "Save page". Reloading
 * restores the build via the normal `getInlineXYZ` loader.
 *
 *   - Standalone `/playground.html`: updates its own address bar.
 *   - Embedded in the landing page (same-origin iframe): posts the value up via
 *     `MOLXYZ_MESSAGE_TYPE` so it lands in the *visible* (parent) address bar.
 *
 * @module loader/sandboxUrlSync
 */

import { serializeXYZ, readCameraView, readBiasesForExport, type CameraViewLike, type BiasPair } from './xyzSerializer';
import { base64EncodeUtf8 } from './urlParser';

/** Apply (or remove) the `molxyz` value to a `location.search` string. Pure. */
export function applyMolxyzToSearch(search: string, value: string | null): string {
  const params = new URLSearchParams(search || '');
  if (value) params.set('molxyz', value);
  else params.delete('molxyz');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Compute the `molxyz` param value (base64 XYZ) for the current atoms, or
 * `null` when there are no atoms (caller removes the param). Pure.
 */
export function computeMolxyzValue(
  elements: ReadonlyArray<string>,
  positions: ReadonlyArray<{ x: number; y: number; z: number }>,
  camera?: CameraViewLike | null,
  biases?: ReadonlyArray<BiasPair> | null,
): string | null {
  if (!elements || elements.length === 0) return null;
  return base64EncodeUtf8(serializeXYZ(elements, positions, { camera, biases }));
}

interface SyncViewer {
  molState: { elements: string[]; positions: Array<{ x: number; y: number; z: number }> };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface SandboxUrlSyncOptions {
  /** Skip writing while this returns true (e.g. builder game). The browser's
   *  default save is still captured. */
  shouldSkip?: () => boolean;
  /** Injectable URL writer (defaults to history.replaceState / postMessage).
   *  When provided, both Ctrl+S saves and lifecycle URL-cleans use it. */
  writeUrl?: (value: string | null) => void;
}

/** Message type the embedded playground posts to its landing-page parent. */
export const MOLXYZ_MESSAGE_TYPE = 'mlipview:molxyz';

/** The molecule-source params a clean removes (the saved sandbox build, plus
 *  any deep-link source so a cleared/loaded scene gets a fresh URL). */
const CLEANED_PARAMS = ['molxyz', 'load', 'smiles'];

/** True when the page was opened with a saved sandbox build (`?molxyz=`). */
function openedWithMolxyz(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('molxyz');
  } catch {
    return false;
  }
}

function defaultWriteUrl(value: string | null): void {
  if (typeof window === 'undefined') return;
  // Embedded (same-origin iframe): the visible URL is the parent's, so post the
  // value up for the landing page to reflect. `null` removes the param.
  if (window.parent && window.parent !== window) {
    try {
      window.parent.postMessage({ type: MOLXYZ_MESSAGE_TYPE, value }, window.location.origin);
    } catch {
      /* cross-origin parent — nothing we can do */
    }
    return;
  }
  // Standalone /playground.html: update our own address bar.
  if (!window.history?.replaceState) return;
  const params = new URLSearchParams(window.location.search);
  if (value) {
    params.set('molxyz', value);
  } else {
    for (const k of CLEANED_PARAMS) params.delete(k);
  }
  const qs = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
}

// Module-level lifecycle state. There is one playground per page, so a
// singleton is correct; `applyMoleculeToViewer` and the keydown handler both
// consult it.
let saveEnabled = false;
let firstSystemApplied = false;
let writer: (value: string | null) => void = defaultWriteUrl;

// Whether the free-build sandbox is the active scene — i.e. opened with a
// saved `?molxyz=` build, or entered via "Clear playground", or after an .xyz
// drop. Drives the settings "Export playground" button (so it appears right
// after a Clear, not only after a Ctrl+S save). Tracked as state (not read from
// window.location) so it is correct in the embedded iframe too, where the
// molxyz lives in the parent's URL. Observable so the button re-renders.
let molxyzActive = false;
const molxyzListeners = new Set<() => void>();

function setMolxyzActive(active: boolean): void {
  if (molxyzActive === active) return;
  molxyzActive = active;
  molxyzListeners.forEach((cb) => cb());
}

/** Whether a `?molxyz=` sandbox build is currently active (export available). */
export function getMolxyzActive(): boolean {
  return molxyzActive;
}

/** Subscribe to molxyz-active changes (for useSyncExternalStore). */
export function subscribeMolxyz(cb: () => void): () => void {
  molxyzListeners.add(cb);
  return () => molxyzListeners.delete(cb);
}

/** Whether the Ctrl/Cmd+S save is currently armed (after a Clear). */
export function isSandboxSaveEnabled(): boolean {
  return saveEnabled;
}

/**
 * Called by `applyMoleculeToViewer` for every clear/load. A clear arms the save
 * shortcut; any load (molecule / tutorial) disarms it. User-initiated
 * clears/loads also clean the saved-build URL params.
 *
 * The *initial* page-load application is exempt from cleaning, so opening a
 * shared `?molxyz=` (or `?load=` / `?smiles=`) link preserves the URL the page
 * was opened with. Additionally, if that initial URL carried a saved sandbox
 * build (`?molxyz=`), save is armed immediately so the user can keep editing
 * and re-saving with Ctrl/Cmd+S without having to Clear first.
 */
export function notifySystemApplied(isClear: boolean): void {
  const isInitial = !firstSystemApplied;
  firstSystemApplied = true;
  saveEnabled = isClear || (isInitial && openedWithMolxyz());
  if (isInitial) {
    // Opened with a saved build → export is available immediately.
    setMolxyzActive(openedWithMolxyz());
  } else {
    // Any subsequent clear/load cleans the saved-build URL params.
    writer(null);
    // A Clear enters the free-build sandbox — export/save become available
    // right away (before any Ctrl+S), so the settings-menu "Export playground"
    // button shows. Any other load leaves the sandbox → export hidden.
    setMolxyzActive(isClear);
  }
}

/**
 * Adopt the given structure as the current sandbox build: write `?molxyz=`, arm
 * the save shortcut, and mark the export as available. Used after a drag-and-
 * drop XYZ load so the dropped build behaves like a `?molxyz=` scene (editable,
 * savable, exportable) even though it came from a file.
 */
export function adoptAsSandboxBuild(
  elements: ReadonlyArray<string>,
  positions: ReadonlyArray<{ x: number; y: number; z: number }>,
  camera?: CameraViewLike | null,
  biases?: ReadonlyArray<BiasPair> | null,
): void {
  const value = computeMolxyzValue(elements, positions, camera, biases);
  writer(value);
  firstSystemApplied = true;
  saveEnabled = true;
  setMolxyzActive(value !== null);
}

/** True for a Save shortcut: Ctrl+S (Win/Linux) or Cmd+S (macOS). */
function isSaveShortcut(e: KeyboardEvent): boolean {
  return (e.key === 's' || e.key === 'S') && (e.metaKey || e.ctrlKey) && !e.altKey;
}

/**
 * Install the Ctrl/Cmd+S → save-to-URL handler on a viewer handle. Returns a
 * disposer that detaches the listener.
 */
export function installSandboxUrlSync(
  viewer: SyncViewer,
  options: SandboxUrlSyncOptions = {},
): () => void {
  const molState = viewer?.molState;
  if (!molState || typeof window === 'undefined') return () => undefined;

  if (options.writeUrl) writer = options.writeUrl;
  const shouldSkip = options.shouldSkip ?? (() => false);

  const onKeyDown = (e: KeyboardEvent): void => {
    if (!isSaveShortcut(e)) return;
    // Capture the browser's default "Save page" dialog whenever we're in the
    // playground, even when saving is disabled.
    e.preventDefault();
    e.stopPropagation();
    if (!saveEnabled || shouldSkip()) return;
    // Include the current camera pose + active tractor beams so a reload
    // restores the view and the beams.
    const value = computeMolxyzValue(
      molState.elements ?? [],
      molState.positions ?? [],
      readCameraView(viewer.camera),
      readBiasesForExport(molState),
    );
    writer(value);
    // A save writes (or clears, when empty) the molxyz build → reflect export
    // availability.
    setMolxyzActive(value !== null);
  };

  window.addEventListener('keydown', onKeyDown, { capture: true });
  return () => window.removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
}

/** Test-only: reset module lifecycle state. */
export function __resetSandboxUrlSyncForTests(): void {
  saveEnabled = false;
  firstSystemApplied = false;
  writer = defaultWriteUrl;
  molxyzActive = false;
  molxyzListeners.clear();
}
